import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface CTASectionProps {
  title: string;
  subtitle?: string;
  primaryButtonText: string;
  primaryButtonLink?: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  variant?: 'yellow' | 'blue' | 'white';
  className?: string;
}

const CTASection: React.FC<CTASectionProps> = ({
  title,
  subtitle,
  primaryButtonText,
  primaryButtonLink = '/auth?mode=signup',
  secondaryButtonText,
  secondaryButtonLink,
  variant = 'yellow',
  className = ''
}) => {
  const navigate = useNavigate();

  const variantStyles = {
    yellow: {
      bg: 'bg-brand-yellow',
      title: 'text-brand-blue',
      subtitle: 'text-brand-blue/80',
      primaryBtn: 'bg-brand-blue text-white hover:bg-brand-blue-700',
      secondaryBtn: 'bg-transparent border-2 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white'
    },
    blue: {
      bg: 'bg-brand-blue',
      title: 'text-white',
      subtitle: 'text-white/80',
      primaryBtn: 'bg-brand-yellow text-brand-blue hover:bg-brand-yellow-400',
      secondaryBtn: 'bg-transparent border-2 border-white text-white hover:bg-white hover:text-brand-blue'
    },
    white: {
      bg: 'bg-white',
      title: 'text-brand-blue',
      subtitle: 'text-gray-600',
      primaryBtn: 'bg-brand-blue text-white hover:bg-brand-blue-700',
      secondaryBtn: 'bg-transparent border-2 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white'
    }
  };

  const styles = variantStyles[variant];

  return (
    <section className={`${styles.bg} py-20 px-4 ${className}`}>
      <div className="max-w-4xl mx-auto text-center">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-6 ${styles.title}`}
        >
          {title}
        </motion.h2>
        
        {subtitle && (
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className={`text-lg md:text-xl mb-10 max-w-2xl mx-auto ${styles.subtitle}`}
          >
            {subtitle}
          </motion.p>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <button
            onClick={() => navigate(primaryButtonLink)}
            className={`px-8 py-4 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl ${styles.primaryBtn}`}
          >
            {primaryButtonText}
          </button>
          
          {secondaryButtonText && secondaryButtonLink && (
            <button
              onClick={() => navigate(secondaryButtonLink)}
              className={`px-8 py-4 rounded-xl font-semibold transition-all duration-200 ${styles.secondaryBtn}`}
            >
              {secondaryButtonText}
            </button>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;

